/**
 * DetailsStep Component
 * Step 4: Optional program details
 */

import React from 'react';
import { FaFileAlt, FaUsers, FaPlus, FaTrash, FaClock, FaCheck } from 'react-icons/fa';
import styles from './StepStyles.module.css';
import detailsStyles from './DetailsStep.module.css';

const DetailsStep = ({
  formData,
  errors,
  updateField,
  updateArrayItem,
  addArrayItem,
  removeArrayItem
}) => {
  // Add staff member
  const addStaff = () => {
    updateField('staff', [
      ...formData.staff,
      { role: '', name: '', email: '', cell: '', profileLink: '' }
    ]);
  };

  // Update staff field
  const updateStaff = (index, field, value) => {
    const newStaff = [...formData.staff];
    newStaff[index] = { ...newStaff[index], [field]: value };
    updateField('staff', newStaff);
  };

  // Remove staff member
  const removeStaff = (index) => {
    updateField('staff', formData.staff.filter((_, i) => i !== index));
  };

  // Toggle waitlist
  const handleWaitlistToggle = () => {
    updateField('capacity.waitlistEnabled', !formData.capacity.waitlistEnabled);
    if (!formData.capacity.waitlistEnabled) {
      updateField('capacity.waitlistLimit', '');
    }
  };

  return (
    <div className={styles.stepContainer}>
      {/* Optional Notice */}
      <div className={detailsStyles.optionalNotice}>
        <span className={detailsStyles.optionalIcon}>💡</span>
        <p>All fields on this page are optional. You can always add or edit these details later.</p>
      </div>

      {/* Program Description */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaFileAlt /> Program Description
        </h3>

        <div className={styles.formGroup}>
          <label className={styles.label}>Description</label>
          <textarea
            className={styles.textarea}
            value={formData.programDetails}
            onChange={(e) => updateField('programDetails', e.target.value)}
            placeholder="Describe what participants can expect from this program..."
            rows={5}
          />
          <span className={styles.hint}>
            A good description helps parents understand what to expect
          </span>
        </div>
      </div>

      {/* Capacity Settings */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaUsers /> Capacity Settings
        </h3>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Maximum Participants</label>
            <input
              type="number"
              className={styles.input}
              value={formData.capacity.maxParticipants}
              onChange={(e) => updateField('capacity.maxParticipants', e.target.value)}
              placeholder="Leave empty for unlimited"
              min="1"
            />
            <span className={styles.hint}>Leave blank for no limit</span>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Minimum Participants</label>
            <input
              type="number"
              className={styles.input}
              value={formData.capacity.minParticipants}
              onChange={(e) => updateField('capacity.minParticipants', e.target.value)}
              placeholder="1"
              min="1"
            />
            <span className={styles.hint}>Minimum needed to run program</span>
          </div>
        </div>

        {/* Waitlist Toggle */}
        <div className={styles.toggleGroup}>
          <div className={styles.toggleInfo}>
            <span className={styles.toggleTitle}>
              <FaClock style={{ marginRight: '8px', color: 'var(--accent)' }} />
              Enable Waitlist
            </span>
            <span className={styles.toggleDesc}>
              Allow participants to join a waitlist when the program is full
            </span>
          </div>
          <div
            className={`${styles.toggle} ${formData.capacity.waitlistEnabled ? styles.active : ''}`}
            onClick={handleWaitlistToggle}
          >
            <div className={styles.toggleKnob} />
          </div>
        </div>

        {/* Waitlist Options */}
        {formData.capacity.waitlistEnabled && (
          <div className={detailsStyles.waitlistOptions}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Waitlist Limit</label>
              <input
                type="number"
                className={styles.input}
                value={formData.capacity.waitlistLimit}
                onChange={(e) => updateField('capacity.waitlistLimit', e.target.value)}
                placeholder="Unlimited"
                min="1"
              />
              <span className={styles.hint}>Maximum people on waitlist</span>
            </div>

            <div className={styles.toggleGroup}>
              <div className={styles.toggleInfo}>
                <span className={styles.toggleTitle}>Auto-enroll from waitlist</span>
                <span className={styles.toggleDesc}>
                  Automatically move waitlisted participants when spots open
                </span>
              </div>
              <div
                className={`${styles.toggle} ${formData.capacity.autoEnrollFromWaitlist ? styles.active : ''}`}
                onClick={() => updateField('capacity.autoEnrollFromWaitlist', !formData.capacity.autoEnrollFromWaitlist)}
              >
                <div className={styles.toggleKnob} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Staff */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaUsers /> Staff
        </h3>
        <p className={styles.hint} style={{ marginTop: '-12px', marginBottom: '16px' }}>
          Add coaches and staff members (all fields optional)
        </p>

        {formData.staff.map((member, index) => (
          <div key={index} className={styles.arrayItem}>
            <button
              type="button"
              className={styles.removeItemBtn}
              onClick={() => removeStaff(index)}
            >
              <FaTrash />
            </button>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Role</label>
                <input
                  type="text"
                  className={styles.input}
                  value={member.role}
                  onChange={(e) => updateStaff(index, 'role', e.target.value)}
                  placeholder="e.g., Head Coach"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={member.name}
                  onChange={(e) => updateStaff(index, 'name', e.target.value)}
                  placeholder="Full name"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <input
                  type="email"
                  className={styles.input}
                  value={member.email}
                  onChange={(e) => updateStaff(index, 'email', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Phone</label>
                <input
                  type="tel"
                  className={styles.input}
                  value={member.cell}
                  onChange={(e) => updateStaff(index, 'cell', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addItemBtn}
          onClick={addStaff}
        >
          <FaPlus /> Add Staff Member
        </button>
      </div>
    </div>
  );
};

export default DetailsStep;









